import { combineReducers, createSlice } from 'redux';
import { getFromIndexedDB, removeFromIndexedDB } from '../Utils/IndexedDBUtils';
import {
  getConnFieldPrefsFromConfig, getDefaultConnFieldPrefs, updateConnFieldPrefsInConfig, updateSingleConnFieldPref, getMasterDataFromConfig,
  getStorageData, setStorageData, getConfigData, STORAGE_KEYS, getActiveData
} from '../Utils/Storage';
import { getAgencyData, setAgencyData } from '../Utils/Storage';
import { getAuthData } from '../Utils/AuthUtils';
import { SET_GLOBAL_LOADING } from './Actions';
import { ArrowLeft } from 'lucide-react';
import { createSelector } from 'reselect';

// Master data keys mapping
export const MASTER_DATA_KEYS = {
  sources: 'Sources',
  cities: 'Cities',
  connExtraFields: 'ConnFields',
  thrExtraFields: 'ThrFields',
  rejectionReasons: 'RejRsns',
  disqlReasons: 'DisqlRsns',
  inactiveReasons: 'InactRsns',
  segments: 'Segments',
  taskTypes: 'TaskTypes',
  products: 'Products',
  threadTypes: 'ThreadTypes',
  recordTypes: 'RecTypes',
  ownEmail: 'OwnEmail',
  ownEmailPwd: 'OwnEmailPwd',
  isOAuthEnabled: 'IsOAuthEnabled',
  contractTypes: 'ContractTypes',
  regions: 'Regions',
  googleReviewLink: 'GoogleLink',
  threadStages: 'ThreadStages',
  fbUserID: 'FBUserID',
  fbUserToken: 'FBUserToken',
  fbLongLivedToken: 'FBLongLivedToken',
  allowSecConns: 'AllowSecConns',
  allowDupInq: 'AllowDupInq',
  wdConfig: 'WDConfig'
};

// Get thread field preferences from localStorage/config using threadID
export const getThreadFieldPrefsFromConfig = (threadID) => {
  try {
    // Get the ThreadFieldPrefs data (not ThreadFieldPrefs_${threadID})
    const threadFieldPrefsData = getMasterDataFromConfig('ThreadFieldPrefs');

    if (!threadFieldPrefsData || !Array.isArray(threadFieldPrefsData)) {
      return getDefaultThreadFieldPrefs();
    }

    // Find the preferences for the specific threadID
    const threadPrefs = threadFieldPrefsData.find(item => item.TID === parseInt(threadID));

    if (threadPrefs && threadPrefs.Prefs) {
      // Handle case where Prefs might be stored as a string
      if (typeof threadPrefs.Prefs === 'string') {
        try {
          return JSON.parse(threadPrefs.Prefs);
        } catch (e) {
          console.error(`Error parsing Prefs for threadID ${threadID}:`, e);
          return getDefaultThreadFieldPrefs();
        }
      }
      return threadPrefs.Prefs;
    }

    return getDefaultThreadFieldPrefs();
  } catch (error) {
    console.error(`Error getting thread field preferences for threadID ${threadID}:`, error);
    return getDefaultThreadFieldPrefs();
  }
};
// Get default thread field preferences
const getDefaultThreadFieldPrefs = () => ({
  POC: false,
  Item: false,
  Ref: false,
  Qty: false,
  AsgnTo: false,
  NextTsk: false,
  Since: false,
  Source: false
});


export const updateThreadFieldPrefsInConfig = (preferences, threadID) => {
  try {
    const configData = getConfigData();

    // Get current ThreadFieldPrefs data
    let threadFieldPrefsData = [];
    if (configData.ThreadFieldPrefs) {
      if (typeof configData.ThreadFieldPrefs === 'string') {
        threadFieldPrefsData = JSON.parse(configData.ThreadFieldPrefs);
      } else if (Array.isArray(configData.ThreadFieldPrefs)) {
        threadFieldPrefsData = configData.ThreadFieldPrefs;
      }
    }

    // Find existing entry for this threadID
    const existingIndex = threadFieldPrefsData.findIndex(item => item.TID === parseInt(threadID));

    const newEntry = {
      TID: parseInt(threadID),
      Prefs: preferences // Store as object, not string
    };

    if (existingIndex !== -1) {
      // Update existing entry
      threadFieldPrefsData[existingIndex] = newEntry;
    } else {
      // Add new entry
      threadFieldPrefsData.push(newEntry);
    }

    // Store back as JSON string
    configData.ThreadFieldPrefs = JSON.stringify(threadFieldPrefsData);

    setStorageData(STORAGE_KEYS.CONFIG_DATA, configData);
    return true;
  } catch (error) {
    console.error(`Error updating thread field preferences for threadID ${threadID}:`, error);
    return false;
  }
};

// Get active data from LocalStorage
const getInitialActiveData = async () => {
  try {
    const activeData = getActiveData();
    return { data: activeData, loading: false, error: null };
  } catch (error) {
    console.error('Error fetching initial agency data:', error);
    return { data: null, loading: false, error: error.message };
  }
};

// Get active data from LocalStorage
const getInitialHeaderConfig = async () => {
  try {
    let headerObj = {
      title: 'Dashboard',
      iconLeft: {
        icon: ArrowLeft,
        onClick: () => null,
      },
      actionsRight: [],
    };
    return { data: headerObj, loading: false, error: null };
  } catch (error) {
    console.error('Error fetching initial agency data:', error);
    return { data: null, loading: false, error: error.message };
  }
};

// IndexedDB stores connections/threads for all agencies; keep Redux state scoped to the active agency only
const filterConnsByActiveAgency = (conns) => {
  const activeAgencyID = getAgencyData()?.AgencyID;
  if (activeAgencyID === undefined || activeAgencyID === null) return conns;
  return (conns || []).filter(conn => Number(conn.AgencyID || conn.agencyID || 0) === Number(activeAgencyID));
};

const filterThreadsByActiveAgency = (threads) => {
  const activeAgencyID = getAgencyData()?.AgencyID;
  if (activeAgencyID === undefined || activeAgencyID === null) return threads;
  return (threads || []).filter(thr => Number(thr.AgencyID || thr.agencyID || 0) === Number(activeAgencyID));
};

// Contracts don't have a single AgencyID field - they belong to a buyer agency and/or a seller agency
const filterContractsByActiveAgency = (contracts) => {
  const activeAgencyID = getAgencyData()?.AgencyID;
  if (activeAgencyID === undefined || activeAgencyID === null) return contracts;
  return (contracts || []).filter(ctrc =>
    Number(ctrc.BuyerAgencyID || 0) === Number(activeAgencyID) ||
    Number(ctrc.SellerAgencyID || 0) === Number(activeAgencyID)
  );
};


const purgeAgencyLessRecords = async (storeName, records) => {
  const orphans = (records || []).filter(r => r?.AgencyID === undefined || r?.AgencyID === null);
  if (orphans.length > 0) {
    console.warn(`Purging ${orphans.length} legacy ${storeName} record(s) with no AgencyID from IndexedDB`);
    try {
      await removeFromIndexedDB(storeName, orphans);
    } catch (error) {
      console.error(`Error purging agency-less ${storeName} records:`, error);
    }
  }
};

// Get action data from IndexedDB and merge with connection data
const getInitialActionData = async () => {
  try {
    const rawThreads = await getFromIndexedDB('Threads') || [];
    const rawConns = await getFromIndexedDB('Conns') || [];
    await Promise.all([
      purgeAgencyLessRecords('Threads', rawThreads),
      purgeAgencyLessRecords('Conns', rawConns),
    ]);
    const actsData = filterThreadsByActiveAgency(rawThreads);
    const connsData = filterConnsByActiveAgency(rawConns);

    const connectionsMap = new Map();
    connsData.forEach(conn => {
      const connId = conn.ConnId || conn.ConnID || conn.connectionId || conn.id;
      if (connId) {
        connectionsMap.set(connId, conn);
        connectionsMap.set(String(connId), conn);
      }
    });

    const enrichedThreadsData = actsData.map(act => {
      const actConnId = act.ConnId || act.ConnID || act.connectionId || act.connection_id;

      if (!actConnId) return act;

      const matchingConn = connectionsMap.get(actConnId) ||
        connectionsMap.get(String(actConnId)) ||
        connectionsMap.get(Number(actConnId));

      return matchingConn ? { ...act, connectionData: matchingConn } : act;
    });

    return { data: enrichedThreadsData, loading: false, error: null };
  } catch (error) {
    console.error('Error fetching initial action data:', error);
    return { data: [], loading: false, error: error.message };
  }
};
// Get contracts data from IndexedDB
const getInitialContractsData = async () => {
  try {
    const rawContracts = await getFromIndexedDB('Contracts') || [];
    const data = filterContractsByActiveAgency(rawContracts);
    return { data, loading: false, error: null };
  } catch (error) {
    console.error('Error fetching initial contracts data:', error);
    return { data: [], loading: false, error: error.message };
  }
};

// Get connection data from IndexedDB
const getInitialConnectionData = async () => {
  try {
    const rawIDBConns = await getFromIndexedDB('Conns') || [];
    await purgeAgencyLessRecords('Conns', rawIDBConns);
    const data = filterConnsByActiveAgency(rawIDBConns);
    return { data, loading: false, error: null };
  } catch (error) {
    console.error('Error fetching initial contact data:', error);
    return { data: [], loading: false, error: error.message };
  }
};

// Get task data from IndexedDB
const getInitialTaskData = async () => {
  try {
    const data = await getFromIndexedDB('Tasks') || [];
    return { data, loading: false, error: null };
  } catch (error) {
    console.error('Error fetching initial task data:', error);
    return { data: [], loading: false, error: error.message };
  }
};

// Get initial master data from localStorage
const getInitialMasterData = async () => {
  try {
    const masterData = {};
    const configData = getConfigData();
    Object.keys(MASTER_DATA_KEYS).forEach(key => {
      const storageKey = MASTER_DATA_KEYS[key];
      try {
        if (key === 'products') {
          const products = getMasterDataFromConfig(storageKey);
          masterData[key] = products.filter(product =>
            product.IsD === false || product.IsD === 0
          );
        } else if (key === 'isOAuthEnabled') {
          // Handle isOAuthEnabled directly from configData as boolean
          masterData[key] = configData.IsOAuthEnabled === true || configData.IsOAuthEnabled === 'true';
        } else {
          masterData[key] = getMasterDataFromConfig(storageKey);
        }
      } catch (error) {
        console.error(`Error loading ${key} from storage:`, error);
        masterData[key] = [];
      }
    });

    // Check for inquiry-only mode and update threadTypes if needed
    if (masterData.threadTypes) {
      const formatName = (name) => {
        if (name === "Order") return "Sale";
        if (name === "Orders") return "Sales";
        if (name === "order") return "sale";
        if (name === "orders") return "sales";
        return name;
      };
      masterData.threadTypes = masterData.threadTypes.map(thread => ({
        ...thread,
        Typ: formatName(thread.Typ),
        TypSng: formatName(thread.TypSng),
        SnSng: formatName(thread.SnSng),
        Sn: formatName(thread.Sn)
      }));

      const shownThreads = masterData.threadTypes.filter(t => t?.Sh === true);
      const isInqOnly = shownThreads.length === 1 && Number(shownThreads[0]?.TID || 0) === 1;
      if (isInqOnly) {
        masterData.threadTypes = masterData.threadTypes.map(thread =>
          Number(thread?.TID || 0) === 1 || Number(thread?.TID || 0) === 2 ? { ...thread, Typ: thread.Sn || thread.Typ, TypSng: thread.SnSng || thread.TypSng } : thread
        );
      }
    }

    masterData.connExtraFieldsArr = getMasterDataFromConfig('ConnFields', true); // Load full object array
    masterData.bizTypes = configData.BizTypes || '';
    masterData.sellToDesc = configData.Sells || '';
    masterData.tagline = configData.Tagline || '';
    masterData.description = configData.Description || '';
    masterData.locLink = configData.LocLink || '';
    masterData.industryID = configData.IndustryID || 0;
    masterData.segmentID = configData.SegmentID || 0;
    masterData.connColPrefs = configData.ConnColPrefs ? (JSON.parse(configData.ConnColPrefs)[0] || {}) : {};
    masterData.thrColPrefs = configData.ThrColPrefs ? (JSON.parse(configData.ThrColPrefs)[0] || {}) : {};

    return {
      ...masterData,
      loading: false,
      error: null
    };
  } catch (error) {
    console.error('Error fetching initial master data:', error);
    return {
      products: [],
      sources: [],
      cities: [],
      connExtraFields: [],
      thrExtraFields: [],
      rejectionReasons: [],
      disqlReasons: [],
      inactiveReasons: [],
      segments: [],
      taskTypes: [],
      threadTypes: [],
      bizTypes: '',
      sellToDesc: '',
      tagline: '',
      description: '',
      locLink: '',
      industryID: 0,
      segmentID: 0,
      loading: false,
      error: error.message,
      recordTypes: [],
      wdConfig: []
    };
  }
};

// Get initial users data from localStorage
const getInitialUsersState = async () => {
  try {
    const usersData = await getStorageData('U') || [];
    const activeUsers = usersData.filter(user => user.IsRemove === 0);
    return { data: activeUsers, loading: false, error: null };
  } catch (error) {
    console.error('Error fetching initial users data:', error);
    return { data: [], loading: false, error: error.message };
  }
};

// Get initial thread field preferences state using threadIDs from masterData
const getInitialThreadFieldPrefsState = async () => {
  try {
    const threadTypes = await getMasterDataFromConfig('ThreadTypes') || [];
    const prefs = {};

    // Start from index 1, not 0
    threadTypes.forEach(thread => {
      const threadID = thread.TID;
      prefs[threadID] = getThreadFieldPrefsFromConfig(threadID);
    });

    return {
      ...prefs,
      loading: false,
      error: null
    };
  } catch (error) {
    console.error('Error auto-loading initial thread field preferences:', error);
    const defaultPrefs = getDefaultThreadFieldPrefs();
    const threadTypes = await getMasterDataFromConfig('ThreadTypes') || [];
    const prefs = threadTypes.reduce((acc, thread) => {
      acc[thread.TID] = defaultPrefs; // Use TID instead of index
      return acc;
    }, {});
    return {
      ...prefs,
      loading: false,
      error: error.message
    };
  }
};

export const getAllThreadFieldPrefs = () => {
  try {
    return getMasterDataFromConfig('ThreadFieldPrefs');
  } catch (error) {
    console.error('Error getting all thread field preferences:', error);
    return [];
  }
};
// Initialize states with async data
const initialLoadingState = {
  isGlobalLoading: false
}

const initialActionState = {
  loading: true,
  error: null,
  ...(await getInitialActionData())
};

const initialConnectionState = {
  loading: true,
  error: null,
  ...(await getInitialConnectionData())
};

const initialTaskState = {
  loading: true,
  error: null,
  ...(await getInitialTaskData())
};

const initialContractsState = {
  loading: true,
  error: null,
  ...(await getInitialContractsData())
};

const initialMasterState = {
  loading: true,
  error: null,
  ...(await getInitialMasterData()),
};

const initialUsersState = {
  loading: true,
  error: null,
  data: [],
  ...(await getInitialUsersState())
};

// Initial Active state
const initialActiveState = {
  loading: true,
  error: null,
  ...(await getInitialActiveData())
}

// Initial Header Config state
const initialHeaderConfigState = {
  loading: true,
  error: null,
  ...(await getInitialHeaderConfig())
}

// Initialize thread field preferences state
const initialThreadFieldPrefsState = await getInitialThreadFieldPrefsState();

// Handle Global loader state
const globalLoaderReducer = (state = initialLoadingState, action) => {
  switch (action.type) {
    case SET_GLOBAL_LOADING:
      return {
        ...state,
        isGlobalLoading: action.payload
      };
    default:
      return state;
  }
};

// Add Active Data reducer
const activeDataReducer = (state = initialActiveState, action) => {
  switch (action.type) {
    case 'UPDATE_ACTIVE_DATA_SUCCESS':
      return { ...state, loading: true, error: null, data: action.payload };
    default:
      return state;
  }
};

// Handle action data state changes
const threadDataReducer = (state = initialActionState, action) => {
  switch (action.type) {
    case 'FETCH_ACTION_DATA_REQUEST':
      return { ...state, loading: true, error: null };
    case 'FETCH_ACTION_DATA_SUCCESS':
      return { ...state, loading: false, data: filterThreadsByActiveAgency(action.payload), error: null };
    case 'FETCH_ACTION_DATA_FAILURE':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_THREAD_DATA_SUCCESS':
      // This is used for successful partial updates/refreshes.
      return { ...state, data: filterThreadsByActiveAgency(action.payload) };
    default:
      return state;
  }
};
// Handle contracts data state changes
const contractsDataReducer = (state = initialContractsState, action) => {
  switch (action.type) {
    case 'FETCH_CONTRACTS_REQUEST':
      return { ...state, loading: true, error: null };
    case 'FETCH_CONTRACTS_SUCCESS':
      return { ...state, loading: false, data: filterContractsByActiveAgency(action.payload), error: null };
    case 'FETCH_CONTRACTS_FAILURE':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};




// Handle connection data state changes
const connectionDataReducer = (state = initialConnectionState, action) => {
  switch (action.type) {
    case 'FETCH_CONNECTIONS_REQUEST':
      return { ...state, loading: true, error: null };
    case 'FETCH_CONNECTIONS_SUCCESS':
      return { ...state, loading: false, data: filterConnsByActiveAgency(action.payload), error: null };
    case 'FETCH_CONNECTIONS_FAILURE':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

// Reducer For Tasks data
const taskDataReducer = (state = initialTaskState, action) => {
  switch (action.type) {
    case 'FETCH_TASKS_REQUEST':
      return { ...state, loading: true, error: null };
    case 'FETCH_TASKS_SUCCESS':
      return { ...state, loading: false, data: action.payload, error: null };
    case 'FETCH_TASKS_FAILURE':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_TASK_DATA_SUCCESS':
      return { ...state, loading: false, data: action.payload, error: null };
    default:
      return state;
  }
};

// Handle master data state changes for all types
const masterDataReducer = (state = initialMasterState, action) => {
  switch (action.type) {
    case 'FETCH_MASTER_DATA_REQUEST':
    case 'FETCH_PRODUCTS_REQUEST':
    case 'ADD_MASTER_ITEM_REQUEST':
    case 'DELETE_MASTER_ITEM_REQUEST':
    case 'ADD_PRODUCT_REQUEST':
    case 'DELETE_PRODUCT_REQUEST':
      return { ...state, loading: true, error: null };
    case 'FETCH_MASTER_DATA_SUCCESS':
      return { ...state, loading: false, [action.payload.type]: action.payload.data };
    case 'FETCH_PRODUCTS_SUCCESS':
      return { ...state, loading: false, products: action.payload };
    case 'ADD_MASTER_ITEM_SUCCESS':
    case 'DELETE_MASTER_ITEM_SUCCESS':
      return { ...state, loading: false, error: null, [action.payload.type]: action.payload.data };
    case 'ADD_PRODUCT_SUCCESS':
    case 'DELETE_PRODUCT_SUCCESS':
      return { ...state, loading: false, error: null };
    case 'FETCH_MASTER_DATA_FAILURE':
    case 'ADD_MASTER_ITEM_FAILURE':
    case 'DELETE_MASTER_ITEM_FAILURE':
    case 'ADD_PRODUCT_FAILURE':
    case 'FETCH_PRODUCTS_FAILURE':
      return { ...state, loading: false, error: action.payload };
    case 'REFRESH_ALL_MASTER_DATA':
      return { ...state, loading: false, error: null };

    // Add this new case to refresh master data when config is updated
    case 'UPDATE_CONFIG_SUCCESS':
      try {

        const refreshedMasterData = {};
        const configData = getConfigData();
        Object.keys(MASTER_DATA_KEYS).forEach(key => {
          const storageKey = MASTER_DATA_KEYS[key];

          try {
            if (key === 'products') {
              const products = getMasterDataFromConfig(storageKey);
              refreshedMasterData[key] = products.filter(product =>
                product.IsD === false || product.IsD === 0
              );
            } else if (key === 'isOAuthEnabled') {
              // Handle isOAuthEnabled directly from configData as boolean
              refreshedMasterData[key] = configData.IsOAuthEnabled === true || configData.IsOAuthEnabled === 'true';
            } else {
              const data = getMasterDataFromConfig(storageKey);
              refreshedMasterData[key] = data;
            }
          } catch (error) {
            console.error(`❌ Error refreshing ${key} from ${storageKey}:`, error);
            refreshedMasterData[key] = state[key] || [];
          }
        });

        // Check for inquiry-only mode and update threadTypes if needed
        if (refreshedMasterData.threadTypes && Array.isArray(refreshedMasterData.threadTypes)) {
          const shownThreads = refreshedMasterData.threadTypes.filter(t => t?.Sh === true);
          const isInqOnly = shownThreads.length === 1 && Number(shownThreads[0]?.TID || 0) === 1;
          if (isInqOnly) {
            refreshedMasterData.threadTypes = refreshedMasterData.threadTypes.map(thread =>
              Number(thread?.TID || 0) === 1 || Number(thread?.TID || 0) === 2 ? { ...thread, Typ: thread.Sn || thread.Typ, TypSng: thread.SnSng || thread.TypSng } : thread
            );
          }
        }

        let updatedConnColPrefs = {};
        let updatedThrColPrefs = {};
        if (configData.ThrColPrefs) {
          try {
            const parsed = JSON.parse(configData.ThrColPrefs);
            updatedThrColPrefs = Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : parsed;
          } catch (error) {
            console.error('Error parsing ConnColPrefs:', error);
            updatedThrColPrefs = state.thrColPrefs || {};
          }
        }
        if (configData.ConnColPrefs) {
          try {
            const parsed = JSON.parse(configData.ConnColPrefs);
            updatedConnColPrefs = Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : parsed;
          } catch (error) {
            console.error('Error parsing ConnColPrefs:', error);
            updatedConnColPrefs = state.connColPrefs || {};
          }
        }

        refreshedMasterData.connExtraFieldsArr = getMasterDataFromConfig('ConnFields', true);
        refreshedMasterData.bizTypes = configData.BizTypes || '';
        refreshedMasterData.sellToDesc = configData.Sells || '';
        refreshedMasterData.tagline = configData.Tagline || '';
        refreshedMasterData.description = configData.Description || '';
        refreshedMasterData.locLink = configData.LocLink || '';
        refreshedMasterData.industryID = configData.IndustryID || 0;
        refreshedMasterData.segmentID = configData.SegmentID || 0;

        const newState = {
          ...state,
          ...refreshedMasterData,
          connColPrefs: updatedConnColPrefs,
          thrColPrefs: updatedThrColPrefs,
          loading: false,
          error: null
        };


        return newState;
      } catch (error) {
        console.error('💥 Error refreshing master data after config update:', error);
        return {
          ...state,
          loading: false,
          error: `Failed to refresh master data: ${error.message}`
        };
      }

    // Add this new case to manually reload master data from localStorage
    case 'RELOAD_MASTER_DATA':
      try {
        const reloadedMasterData = {};
        const configData = getConfigData();

        Object.keys(MASTER_DATA_KEYS).forEach(key => {
          const storageKey = MASTER_DATA_KEYS[key];
          try {
            if (key === 'products') {
              const products = getMasterDataFromConfig(storageKey);
              reloadedMasterData[key] = products.filter(product =>
                product.IsD === false || product.IsD === 0
              );
            } else if (key === 'isOAuthEnabled') {
              // Handle isOAuthEnabled directly from configData as boolean
              reloadedMasterData[key] = configData.IsOAuthEnabled === true || configData.IsOAuthEnabled === 'true';
            } else {
              reloadedMasterData[key] = getMasterDataFromConfig(storageKey);
            }
          } catch (error) {
            console.error(`Error reloading ${key} from localStorage:`, error);
            reloadedMasterData[key] = state[key] || []; // Keep existing data if reload fails
          }
        });

        // Check for inquiry-only mode and update threadTypes if needed
        if (reloadedMasterData.threadTypes && Array.isArray(reloadedMasterData.threadTypes)) {
          const shownThreads = reloadedMasterData.threadTypes.filter(t => t?.Sh === true);
          const isInqOnly = shownThreads.length === 1 && Number(shownThreads[0]?.TID || 0) === 1;
          if (isInqOnly) {
            reloadedMasterData.threadTypes = reloadedMasterData.threadTypes.map(thread =>
              Number(thread?.TID || 0) === 1 || Number(thread?.TID || 0) === 2 ? { ...thread, Typ: thread.Sn || thread.Typ, TypSng: thread.SnSng || thread.TypSng } : thread
            );
          }
        }

        let reloadedConnColPrefs = {};
        if (configData.ConnColPrefs) {
          try {
            const parsed = JSON.parse(configData.ConnColPrefs);
            reloadedConnColPrefs = Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : parsed;
          } catch (error) {
            console.error('Error parsing ConnColPrefs during reload:', error);
            reloadedConnColPrefs = state.connColPrefs || {};
          }
        }
        let reloadedThrColPrefs = {};
        if (configData.ThrColPrefs) {
          try {
            const parsed = JSON.parse(configData.ThrColPrefs);
            reloadedThrColPrefs = Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : parsed;
          } catch (error) {
            console.error('Error parsing ThrColPrefs during reload:', error);
            reloadedThrColPrefs = state.thrColPrefs || {};
          }
        }

        reloadedMasterData.connExtraFieldsArr = getMasterDataFromConfig('ConnFields', true);
        reloadedMasterData.bizTypes = configData.BizTypes || '';
        reloadedMasterData.sellToDesc = configData.Sells || '';
        reloadedMasterData.tagline = configData.Tagline || '';
        reloadedMasterData.description = configData.Description || '';
        reloadedMasterData.locLink = configData.LocLink || '';
        reloadedMasterData.industryID = configData.IndustryID || 0;
        reloadedMasterData.segmentID = configData.SegmentID || 0;

        console.log('Master data reloaded from localStorage:', reloadedMasterData);

        return {
          ...state,
          ...reloadedMasterData,
          connColPrefs: reloadedConnColPrefs,
          thrColPrefs: reloadedThrColPrefs,
          loading: false,
          error: null
        };
      } catch (error) {
        console.error('Error reloading master data from localStorage:', error);
        return {
          ...state,
          loading: false,
          error: `Failed to reload master data: ${error.message}`
        };
      }

    default:
      return state;
  }
};

const headerConfigReducer = (state = initialHeaderConfigState, action) => {
  switch (action.type) {
    case 'FETCH_HEADER_CONFIG_REQUEST':
      return { ...state, loading: true, error: null };
    case 'UPDATE_HEADER_CONFIG_SUCCESS':
      return { ...state, loading: true, error: null, data: action.payload };
    case 'FETCH_HEADER_CONFIG_FAILURE':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

// ==================== Add to Storage.js ====================

// Get SCRM configs from localStorage
export const getSCRMConfigs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SCRM_CONFIGS);
    const data = JSON.parse(raw || '[]');
    return data;
  } catch (err) {
    console.error("Failed to parse SCRMConfigs", err);
    return [];
  }
};

// Get transaction types from SCRM configs
export const getTransactionTypesFromSCRM = () => {
  try {
    const scrmConfigs = getSCRMConfigs();
    const xnTypesConfig = scrmConfigs.find(config => config.Config === 'XnTypes');

    if (xnTypesConfig && xnTypesConfig.Val) {
      const transactionTypes = JSON.parse(xnTypesConfig.Val);
      return transactionTypes;
    }

    return [];
  } catch (error) {
    console.error('Error parsing transaction types from SCRM configs:', error);
    return [];
  }
};

export const getUserConfig = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER_CONFIGS_DATA);
    const data = JSON.parse(raw || '[]');
    const currentUserID = getAuthData()?.UserID;
    const matched = data.find(row => Number(row.UserID || row.NodeID) === Number(currentUserID));
    return matched || data[0];
  } catch (err) {
    console.error("Failed to parse user configs", err);
    return [];
  }
};

// Get User Configs from V cookie
export const getUsersConfigFromV = () => {
  try {
    const scrmConfigs = getSCRMConfigs();
    const xnTypesConfig = scrmConfigs.find(config => config.Config === 'XnTypes');

    if (xnTypesConfig && xnTypesConfig.Val) {
      const transactionTypes = JSON.parse(xnTypesConfig.Val);
      return transactionTypes;
    }

    return [];
  } catch (error) {
    console.error('Error parsing transaction types from SCRM configs:', error);
    return [];
  }
};

// Get CntTypewiseWebstr from SCRM configs
export const getCntTypewiseWebstrFromSCRM = () => {
  try {
    const scrmConfigs = getSCRMConfigs();
    const configItem = scrmConfigs.find(config => config.Config === 'CntTypewiseWebstr');

    if (configItem && configItem.Val) {
      const data = typeof configItem.Val === 'string' ? JSON.parse(configItem.Val) : configItem.Val;
      return Array.isArray(data) ? data : [];
    }

    return [];
  } catch (error) {
    console.error('Error parsing CntTypewiseWebstr from SCRM configs:', error);
    return [];
  }
};

// ==================== Add to Reducers.js ====================

const getInitialSCRMConfigsSync = () => {
  try {
    const transactionTypes = getTransactionTypesFromSCRM();
    const CntTypewiseWebstr = getCntTypewiseWebstrFromSCRM();
    return { transactionTypes, CntTypewiseWebstr, loading: false, error: null };
  } catch (error) {
    console.error('Error fetching initial SCRM configs:', error);
    return { transactionTypes: [], CntTypewiseWebstr: [], loading: false, error: error.message };
  }
};
const getInitialUsersConfig = () => {
  try {
    const userConfig = getUserConfig();
    return { userConfig, loading: false, error: null };
  } catch (error) {
    console.error('Error fetching initial SCRM configs:', error);
    return { usersConfig: [], loading: false, error: error.message };
  }
};

// SCRM Config Reducer
const scrmConfigReducer = (state = initialSCRMConfigState, action) => {
  switch (action.type) {
    case 'RELOAD_SCRM_CONFIGS':
      try {
        const reloadedTransactionTypes = getTransactionTypesFromSCRM();
        const reloadedCntTypewiseWebstr = getCntTypewiseWebstrFromSCRM();
        return {
          ...state,
          transactionTypes: reloadedTransactionTypes,
          CntTypewiseWebstr: reloadedCntTypewiseWebstr,
          loading: false,
          error: null
        };
      } catch (error) {
        return { ...state, loading: false, error: `Failed to reload SCRM configs: ${error.message}` };
      }
    default:
      return state;
  }
};

const userConfigReducer = (state = initialUsersConfigState, action) => {
  switch (action.type) {
    case 'RELOAD_USER_CONFIG':
      try {
        const userConfig = getUserConfig();
        return { ...state, userConfig: userConfig, loading: false, error: null };
      } catch (error) {
        return { ...state, loading: false, error: `Failed to reload SCRM configs: ${error.message}` };
      }
    default:
      return state;
  }
};

const initialUsersConfigState = getInitialUsersConfig();

const initialSCRMConfigState = getInitialSCRMConfigsSync();

// Action creator
export const reloadSCRMConfigs = () => ({ type: 'RELOAD_SCRM_CONFIGS' });

// Selectors
export const getTransactionTypesArr = (state) => state.scrmConfigs?.transactionTypes || [];
export const getCntTypewiseWebstrArr = (state) => state.scrmConfigs?.CntTypewiseWebstr || [];

// Handle users data state changes
const usersReducer = (state = initialUsersState, action) => {
  switch (action.type) {
    case 'UPDATE_USER_DATA_SUCCESS':
      return { ...state, loading: true, error: null, data: action.payload };
    default:
      return state;
  }
};

// Auto-load initial connection field preferences data
const getInitialConnFieldPrefsState = () => {
  try {
    const prefsData = getConnFieldPrefsFromConfig();
    return {
      preferences: prefsData,
      loading: false,
      error: null
    };
  } catch (error) {
    console.error('Error auto-loading initial contact field preferences:', error);
    const defaultPrefs = getDefaultConnFieldPrefs();
    return {
      preferences: defaultPrefs,
      loading: false,
      error: error.message
    };
  }
};

// Initialize with auto-loaded data
const initialConnFieldPrefsState = getInitialConnFieldPrefsState();

// Connection field preferences reducer
const connFieldsPrefsReducers = (state = initialConnFieldPrefsState, action) => {
  switch (action.type) {
    case 'UPDATE_CONN_FIELD_PREF_REQUEST':
      return {
        ...state,
        loading: true,
        error: null
      };

    case 'UPDATE_CONN_FIELD_PREF_SUCCESS':
      return {
        ...state,
        loading: false,
        error: null
      };

    case 'UPDATE_CONN_FIELD_PREF_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    case 'UPDATE_CONN_FIELD_PREF':
      const updatedPrefs = {
        ...state.preferences,
        [action.payload.fieldName]: action.payload.value
      };

      updateSingleConnFieldPref(action.payload.fieldName, action.payload.value);

      return {
        ...state,
        preferences: updatedPrefs,
        error: null
      };

    case 'UPDATE_ALL_CONN_FIELD_PREFS':
      updateConnFieldPrefsInConfig(action.payload);

      return {
        ...state,
        preferences: action.payload,
        error: null
      };

    case 'RESET_CONN_FIELD_PREFS':
      const defaultPrefs = getDefaultConnFieldPrefs();

      updateConnFieldPrefsInConfig(defaultPrefs);

      return {
        ...state,
        preferences: defaultPrefs,
        error: null
      };

    case 'RELOAD_CONN_FIELD_PREFS':
      try {
        let reloadedPrefs;

        if (action.payload && typeof action.payload === 'object') {
          reloadedPrefs = action.payload;
        } else {
          reloadedPrefs = getConnFieldPrefsFromConfig();
        }

        return {
          ...state,
          preferences: reloadedPrefs,
          error: null
        };
      } catch (error) {
        console.error('Error reloading connection field preferences:', error);
        return {
          ...state,
          error: error.message
        };
      }

    default:
      return state;
  }
};

// Thread field preferences reducer using threadID
const threadFieldsPrefsReducer = (state = initialThreadFieldPrefsState, action) => {
  switch (action.type) {
    case 'UPDATE_THREAD_FIELD_PREFS_REQUEST':
      return {
        ...state,
        loading: true,
        error: null
      };

    case 'UPDATE_THREAD_FIELD_PREFS_SUCCESS':
      return {
        ...state,
        loading: false,
        error: null
      };

    case 'UPDATE_THREAD_FIELD_PREFS_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    case 'UPDATE_THREAD_FIELD_PREF':
      const { threadID, fieldName, value } = action.payload;
      const updatedThreadPrefs = {
        ...state[threadID],
        [fieldName]: value
      };

      return {
        ...state,
        [threadID]: updatedThreadPrefs,
        error: null
      };

    case 'UPDATE_ALL_THREAD_FIELD_PREFS':
      const { threadID: updateThreadID, preferences } = action.payload;

      updateThreadFieldPrefsInConfig(preferences, updateThreadID);

      return {
        ...state,
        [updateThreadID]: preferences,
        error: null
      };

    case 'RELOAD_ALL_THREAD_FIELD_PREFS_FROM_SERVER':
      // Handle direct Redux state update from server response
      return {
        ...state,
        ...action.payload,
        loading: false,
        error: null
      };

    case 'RELOAD_ALL_THREAD_FIELD_PREFS':
      const threadTypes = getMasterDataFromConfig('ThreadTypes') || [];
      const reloadedPrefs = {};
      threadTypes.forEach(thread => {
        reloadedPrefs[thread.TID] = getThreadFieldPrefsFromConfig(thread.TID); // Use TID
      });

      return {
        ...state,
        ...reloadedPrefs,
        error: null
      };

    case 'RESET_THREAD_FIELD_PREFS':
      const { threadID: resetThreadID } = action.payload;
      const defaultThreadPrefs = getDefaultThreadFieldPrefs();

      updateThreadFieldPrefsInConfig(defaultThreadPrefs, resetThreadID);

      return {
        ...state,
        [resetThreadID]: defaultThreadPrefs,
        error: null
      };

    case 'RELOAD_THREAD_FIELD_PREFS':
      try {
        const { threadID: reloadThreadID } = action.payload;
        const reloadedPrefs = getThreadFieldPrefsFromConfig(reloadThreadID);

        return {
          ...state,
          [reloadThreadID]: reloadedPrefs,
          error: null
        };
      } catch (error) {
        console.error('Error reloading thread field preferences:', error);
        return {
          ...state,
          error: error.message
        };
      }

    default:
      return state;
  }
};

const getInitialAgencyData = async () => {
  try {
    const agencyData = getAgencyData();
    return { data: agencyData, loading: false, error: null };
  } catch (error) {
    console.error('Error fetching initial agency data:', error);
    return { data: null, loading: false, error: error.message };
  }
};

// Add initial agency state
const initialAgencyState = {
  loading: true,
  error: null,
  data: null,
  ...(await getInitialAgencyData())
};

// Add agency reducer
const agencyDataReducer = (state = initialAgencyState, action) => {
  switch (action.type) {
    case 'FETCH_AGENCY_DATA_REQUEST':
      return { ...state, loading: true, error: null };
    case 'FETCH_AGENCY_DATA_SUCCESS':
      return { ...state, loading: false, data: action.payload, error: null };
    case 'FETCH_AGENCY_DATA_FAILURE':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_AGENCY_DATA_SUCCESS':
      return { ...state, loading: false, data: action.payload, error: null };
    default:
      return state;
  }
};

// Add agency action creators
export const fetchAgencyData = () => async (dispatch) => {
  dispatch({ type: 'FETCH_AGENCY_DATA_REQUEST' });
  try {
    const agencyData = getAgencyData();
    dispatch({ type: 'FETCH_AGENCY_DATA_SUCCESS', payload: agencyData });
  } catch (error) {
    dispatch({ type: 'FETCH_AGENCY_DATA_FAILURE', payload: error.message });
  }
};

export const updateAgencyData = (agencyData) => async (dispatch) => {
  dispatch({ type: 'FETCH_AGENCY_DATA_REQUEST' });
  try {
    setAgencyData(agencyData);
    dispatch({ type: 'UPDATE_AGENCY_DATA_SUCCESS', payload: agencyData });
  } catch (error) {
    dispatch({ type: 'FETCH_AGENCY_DATA_FAILURE', payload: error.message });
  }
};

export const updateHeaderConfig = (headerData) => async (dispatch) => {
  dispatch({ type: 'FETCH_HEADER_CONFIG_REQUEST' });
  try {
    // setAgencyData(headerData);
    dispatch({ type: 'UPDATE_HEADER_CONFIG_SUCCESS', payload: headerData });
  } catch (error) {
    dispatch({ type: 'FETCH_HEADER_CONFIG_FAILURE', payload: error.message });
  }
};

export const getAgencyDetails = (state) =>
  state.agencyData?.data;

export const isSmartSellerMode = () => {
  try {
    const data = getAgencyData(); // Uses your existing Utils/Storage import
    return data?.CntMaxUsers === 1;
  } catch (error) {
    return false;
  }
}

export const userIsAdmin = (state) => {
  const loginUserID = getAuthData();
  const isCreator = state.agencyData?.data?.AdminUserID === loginUserID?.UserID;
  const currentUserRow = (state.users?.data || []).find(user => user.UserID === loginUserID?.UserID);
  const isAdminUser = currentUserRow?.IsAdmin === true || currentUserRow?.IsAdmin === 1;
  return isCreator || isAdminUser;
}

export const userIsCreator = (state) => {
  const loginUserID = getAuthData();
  return state.agencyData?.data?.AdminUserID === loginUserID?.UserID;
}

export const isAgencyAllowed = (state) => {
  const allowedAgencyIDs = [1, 3];
  return allowedAgencyIDs.includes(state.agencyData?.data?.AgencyID);
}

export const isAgencyAllowedFutureDT = (state) => {
  const allowedAgencyID = [13];
  return allowedAgencyID.includes(state.agencyData?.data?.AgencyID);
}

export const checkIfInqOnlyMode = (state) => {
  let isInqOnly = false;
  let thrTypes = state.masterData?.threadTypes || [];
  let shownThrs = thrTypes.filter(t => t?.Sh);
  if (shownThrs.length === 1 && Number(shownThrs[0]?.TID || 0) === 1) {
    isInqOnly = true;
  }
  return isInqOnly;
}

// ==================== ACTION CREATORS ====================

// Connection field preferences action creators
export const updateConnFieldPref = (fieldName, value) => ({
  type: 'UPDATE_CONN_FIELD_PREF',
  payload: { fieldName, value }
});

export const updateAllConnFieldPrefs = (preferences) => ({
  type: 'UPDATE_ALL_CONN_FIELD_PREFS',
  payload: preferences
});

export const resetConnFieldPrefs = () => ({
  type: 'RESET_CONN_FIELD_PREFS'
});

export const reloadConnFieldPrefs = () => ({
  type: 'RELOAD_CONN_FIELD_PREFS'
});

// Thread field preferences action creators
export const updateThreadFieldPref = (threadID, fieldName, value) => ({
  type: 'UPDATE_THREAD_FIELD_PREF',
  payload: { threadID, fieldName, value }
});

export const updateAllThreadFieldPrefs = (threadID, preferences) => ({
  type: 'UPDATE_ALL_THREAD_FIELD_PREFS',
  payload: { threadID, preferences }
});

export const resetThreadFieldPrefs = (threadID) => ({
  type: 'RESET_THREAD_FIELD_PREFS',
  payload: { threadID }
});

export const reloadThreadFieldPrefs = (threadID) => ({
  type: 'RELOAD_THREAD_FIELD_PREFS',
  payload: { threadID }
});

// Users action creators
export const fetchUsers = () => async (dispatch) => {
  dispatch({ type: 'FETCH_USERS_REQUEST' });
  try {
    const usersData = await getStorageData('U') || [];
    const activeUsers = usersData.filter(user => user.IsRemove === 0);
    dispatch({ type: 'FETCH_USERS_SUCCESS', payload: activeUsers });
  } catch (error) {
    dispatch({ type: 'FETCH_USERS_FAILURE', payload: error.message });
  }
};

// export const updateUser = (userData) => async (dispatch) => {
//   dispatch({ type: 'FETCH_USERS_REQUEST' });
//   try {
//     const currentUsers = await getStorageData('U') || [];
//     const updatedUsers = currentUsers.map(user =>
//       user.UserID === userData.UserID ? { ...user, ...userData } : user
//     );
//     setStorageData('U', updatedUsers);
//     dispatch({ type: 'UPDATE_USER_SUCCESS', payload: userData });
//   } catch (error) {
//     dispatch({ type: 'FETCH_USERS_FAILURE', payload: error.message });
//   }
// };

// export const addUser = (userData) => async (dispatch) => {
//   dispatch({ type: 'FETCH_USERS_REQUEST' });
//   try {
//     const currentUsers = await getStorageData('U') || [];
//     const newUser = {
//       ...userData,
//       UserID: Math.max(...currentUsers.map(u => u.UserID || 0), 0) + 1,
//       NodeID: userData.NodeID || Math.max(...currentUsers.map(u => u.NodeID || 0), 0) + 1,
//       IsRemove: 0,
//       Tbl: 'U'
//     };
//     const updatedUsers = [...currentUsers, newUser];
//     setStorageData('U', updatedUsers);
//     dispatch({ type: 'ADD_USER_SUCCESS', payload: newUser });
//   } catch (error) {
//     dispatch({ type: 'FETCH_USERS_FAILURE', payload: error.message });
//   }
// };

// export const removeUser = (userID) => async (dispatch) => {
//   dispatch({ type: 'FETCH_USERS_REQUEST' });
//   try {
//     const currentUsers = await getStorageData('U') || [];
//     const updatedUsers = currentUsers.map(user =>
//       user.UserID === userID ? { ...user, IsRemove: 1 } : user
//     );
//     setStorageData('U', updatedUsers);
//     dispatch({ type: 'REMOVE_USER_SUCCESS', payload: userID });
//   } catch (error) {
//     dispatch({ type: 'FETCH_USERS_FAILURE', payload: error.message });
//   }
// };

// ==================== SELECTORS ====================

// Connection field preferences selectors
export const selectConnFieldPrefs = (state) =>
  state.connFieldsPrefs?.preferences || {};

export const selectConnFieldPref = (state, fieldName) =>
  state.connFieldsPrefs?.preferences?.[fieldName] || false;

export const selectConnFieldPrefsLoading = (state) =>
  state.connFieldsPrefs?.loading || false;

export const selectConnFieldPrefsError = (state) =>
  state.connFieldsPrefs?.error || null;

export const selectEnabledConnFields = (state) => {
  const prefs = state.connFieldsPrefs?.preferences || {};
  return Object.keys(prefs).filter(key => prefs[key] === true);
};

export const selectDisabledConnFields = (state) => {
  const prefs = state.connFieldsPrefs?.preferences || {};
  return Object.keys(prefs).filter(key => prefs[key] === false);
};

export const selectConnFieldPrefsAsArray = (state) => {
  const prefs = state.connFieldsPrefs?.preferences || {};
  return Object.keys(prefs).map(key => ({
    fieldName: key,
    enabled: prefs[key],
    displayName: key.charAt(0).toUpperCase() + key.slice(1)
  }));
};

// Expense selectors
const TRIGGER_EXPENSES_REFRESH = 'expenses/triggerRefresh';

// Action Creators
export const triggerExpensesRefresh = () => ({
  type: TRIGGER_EXPENSES_REFRESH,
});

// Reducer
const initialState = {
  refreshTrigger: 0,
};

const expensesReducer = (state = initialState, action) => {
  switch (action.type) {
    case TRIGGER_EXPENSES_REFRESH:
      return {
        ...state,
        refreshTrigger: state.refreshTrigger + 1,
      };
    default:
      return state;
  }
};

// Selector
export const getExpensesRefreshTrigger = (state) => state.expenses.refreshTrigger;


// Thread field preferences selectors
export const selectThreadFieldPrefs = (state) => state.threadFieldsPrefs || {};

export const selectThreadFieldPref = (state, fieldName, threadID) =>
  state.threadFieldsPrefs?.[threadID]?.[fieldName] || false;

export const selectThreadFieldPrefsByThreadID = (state, threadID) =>
  state.threadFieldsPrefs?.[threadID] || {};

export const selectEnabledThreadFields = (state, threadID) => {
  const prefs = state.threadFieldsPrefs?.[threadID] || {};
  return Object.keys(prefs).filter(key => prefs[key] === true);
};
export const selectThreadFieldPrefsLoading = (state) =>
  state.threadFieldsPrefs?.loading || false;

export const selectThreadFieldPrefsError = (state) =>
  state.threadFieldsPrefs?.error || null;

export const getThreadTypesArr = (state) =>
  state.masterData?.threadTypes || [];

export const selectConnColPrefs = (state) =>
  state.masterData?.connColPrefs || {};

export const getActiveThreadTypesArr = createSelector([(state) => state.masterData?.threadTypes || []],
  (threadTypes) => {
    return threadTypes.filter(tType => tType?.Sh);
  }
);

export const getThreadTypesArrForSC = createSelector([(state) => state.masterData?.threadTypes || []],
  (threadTypes) => {
    return threadTypes
  }
);

export const getThreadTypeByTID = (tID) => (state) =>
  getThreadTypesArr(state).find((item) => item.TID === tID);



// Users selectors
export const selectUsers = (state) => state.users?.data || [];

export const selectUsersLoading = (state) => state.users?.loading || false;

export const selectUsersError = (state) => state.users?.error || null;

const mapUserRow = (user) => ({
  ...user,
  userID: user.UserID,
  name: user.Name,
  email: user.Email,
  designation: user.Designation,
  mobile: user.Mobile,
  cc: user.CC,
  fullAccess: user?.FullAccess || false,
  isAdmin: user?.IsAdmin || false,
  connViewTypeID: user?.ConnViewTypeID || 0,
  connEntry: user?.ConnEntry || false,
  connEdit: user?.ConnEdit || false,
  connDel: user?.ConnDel || false,
  thrViewTypeID: user?.ThrViewTypeID || 0,
  thrActions: user?.ThrActions || false,
  xnActions: user?.XnActions || false,
  agencyID: user.AgencyID || user.agencyID || 0
});

export const getUsersArr = createSelector(
  [(state) => state.users?.data || [], (state) => state.agencyData?.data?.AgencyID],
  (users, activeAgencyID) => users
    .filter(user => user.IsRemove === 0 && (activeAgencyID === undefined || activeAgencyID === null || Number(user.AgencyID || user.agencyID || 0) === Number(activeAgencyID)))
    .map(mapUserRow)
);

export const getAllUsersArr = createSelector(
  [(state) => state.users?.data || []],
  (users) => users
    .filter(user => user.IsRemove === 0)
    .map(mapUserRow)
);

// Connection data selectors
export const getConnsArr = (state) => {
  return state.connectionData?.data || [];
};

export const selectConnectionData = (state) =>
  state.connectionData?.data || [];

export const selectConnectionDataLoading = (state) =>
  state.connectionData?.loading || false;

export const selectConnectionDataError = (state) =>
  state.connectionData?.error || null;

export const getConnectionByID = (connID) => (state) => {
  if (!connID) return null;
  const connectionData = state.connectionData?.data || [];
  const numericConnID = parseInt(connID);
  return connectionData.find(conn =>
    conn.ConnID === numericConnID ||
    conn.ConnID === connID ||
    conn.NodeID === numericConnID ||
    conn.NodeID === connID
  ) || null;
};

export const getConnectionNames = (state) => {
  const connections = state.connectionData?.data || [];
  return connections
    .filter(conn => conn.BizName)
    .map(conn => conn.BizName);
};
export const getUsernames = (state) => {
  const users = state.users?.data || [];
  return users
    .filter(user => user.ConnEntry === true && user.IsRemove === 0)
    .map(user => user.Name);
};

// Contracts data selectors
export const getContractsArr = (state) => {
  return state.contractsData?.data || [];
};

export const selectContractsData = (state) =>
  state.contractsData?.data || [];

export const selectContractsDataLoading = (state) =>
  state.contractsData?.loading || false;

export const selectContractsDataError = (state) =>
  state.contractsData?.error || null;

export const getContractByID = (contractID) => (state) => {
  if (!contractID) return null;
  const contractsData = state.contractsData?.data || [];
  const numericContractID = parseInt(contractID);
  return contractsData.find(contract =>
    contract.ContractID === numericContractID ||
    contract.ContractID === contractID ||
    contract.NodeID === numericContractID ||
    contract.NodeID === contractID
  ) || null;
};



// Master data selectors
export const selectMasterData = (state) => state.masterData || {};

export const selectMasterDataByType = (state, type) =>
  state.masterData?.[type] || [];

export const selectMasterDataLoading = (state) =>
  state.masterData?.loading || false;

export const selectMasterDataError = (state) =>
  state.masterData?.error || null;

export const selectProducts = (state) =>
  state.masterData?.products || [];

export const selectSources = (state) =>
  state.masterData?.sources || [];

export const selectCities = (state) =>
  state.masterData?.cities || [];

export const selectExtraFields = (state) =>
  state.masterData?.extraFields || [];

export const selectExtraFieldsArr = (state) =>
  state.masterData?.connExtraFieldsArr || [];

export const selectRejectionReasons = (state) =>
  state.masterData?.rejectionReasons || [];

export const selectDisqlReasons = (state) =>
  state.masterData?.disqlReasons || [];

export const selectInactiveReasons = (state) =>
  state.masterData?.inactiveReasons || [];

export const selectSegments = (state) =>
  state.masterData?.segments || [];

// Agency Info selectors
export const selectBizTypes = (state) =>
  state.masterData?.bizTypes || '';

export const selectSellToDesc = (state) =>
  state.masterData?.sellToDesc || '';

export const selectTagline = (state) =>
  state.masterData?.tagline || '';

export const selectDescription = (state) =>
  state.masterData?.description || '';

export const selectLocLink = (state) =>
  state.masterData?.locLink || '';

export const selectIndustryID = (state) =>
  state.masterData?.industryID || 0;

export const selectSegmentID = (state) =>
  state.masterData?.segmentID || 0;

export default combineReducers({
  masterData: masterDataReducer,
  threadData: threadDataReducer,
  connectionData: connectionDataReducer,
  contractsData: contractsDataReducer,
  connFieldsPrefs: connFieldsPrefsReducers,
  threadFieldsPrefs: threadFieldsPrefsReducer,
  users: usersReducer,
  agencyData: agencyDataReducer,
  globalLoader: globalLoaderReducer,
  scrmConfigs: scrmConfigReducer,
  activeData: activeDataReducer,
  headerConfigData: headerConfigReducer,
  taskData: taskDataReducer,
  expenses: expensesReducer,
  userConfig: userConfigReducer
});